import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, Anchor } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1612812746054-7dfda5dd2e27?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHw0fHxvZmZzaG9yZSUyMHdpbmQlMjBmYXJtJTIwb2NlYW58ZW58MHx8fHwxNzg1NzY2MjQ1fDA&ixlib=rb-4.1.0&q=85";

const LINES = ["Powering the", "world's offshore", "frontier."];

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.35 + i * 0.14 },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const scrollDown = () => {
    const el = document.querySelector("#capabilities");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden"
    >
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 z-0">
        <img
          src={HERO_IMG}
          alt="Offshore engineering vessel at sea"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-[#050B14]/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050B14] via-transparent to-[#050B14]/40" />
      </motion.div>

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 lg:px-12 h-[100svh] flex flex-col justify-end pb-20 pt-32">
        <motion.div
          style={{ opacity: fade }}
          className="flex items-center gap-3 mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <Anchor className="h-4 w-4 text-[#FF5A00]" />
          <span className="font-mono-tech text-[11px] uppercase tracking-[0.35em] text-[#94A3B8]">
            Marine Electrical Engineering · Est. Offshore
          </span>
        </motion.div>

        <h1 className="font-serif-display font-medium text-[#F8FAFC] leading-[0.95] text-5xl sm:text-7xl lg:text-[8.5rem] tracking-[-0.02em] max-w-[16ch]">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={lineVariant}
                initial="hidden"
                animate="show"
                className={`block ${i === 1 ? "text-[#FF5A00]" : ""}`}
                data-testid={`hero-line-${i}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          className="mt-10 flex flex-col sm:flex-row sm:items-end gap-8 sm:gap-16"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9 }}
        >
          <p className="font-mono-tech text-sm leading-relaxed text-[#94A3B8] max-w-md">
            High-voltage installations, power distribution and systems automation for
            platforms, wind farms and marine vessels — delivered to international maritime
            safety standards.
          </p>
          <button
            onClick={scrollDown}
            data-testid="hero-cta"
            className="group inline-flex items-center gap-3 border border-white/20 hover:border-[#FF5A00] px-6 py-4 transition-colors duration-300"
          >
            <span className="font-mono-tech text-xs uppercase tracking-[0.25em]">
              Explore Capabilities
            </span>
            <ArrowDownRight className="h-4 w-4 text-[#FF5A00] group-hover:translate-x-1 group-hover:translate-y-1 transition-transform duration-300" />
          </button>
        </motion.div>
      </div>
    </section>
  );
}
